import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSite } from '../../context/SiteContext';
import toast from 'react-hot-toast';
import TeamMembers from '../eightd/TeamMembers';
import FiveWhys from '../eightd/FiveWhys';
import ActionsSection from '../eightd/ActionsSection';
import D6Verification from '../eightd/D6Verification';

const Acciones = () => {

  
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { currentSite, getCurrentSiteId } = useSite();
  const navigate = useNavigate();
  
  // Estados principales
  const [view, setView] = useState('problems'); // problems|eightd
  const [problems, setProblems] = useState([]);
  const [eightdReports, setEightdReports] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [whyTrees, setWhyTrees] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Catálogos
  const [lines, setLines] = useState([]);
  const [defectTypes, setDefectTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Formularios
  const [editingProblem, setEditingProblem] = useState(null);
  const [problemForm, setProblemForm] = useState({
    line_id: '',
    defect_type_id: '',
    product_id: '',
    detected_at: new Date().toISOString().split('T')[0],
    period_start: '',
    period_end: '',
    status: 'open',
    problem_key: '',
    description: ''
  });
  
  // Actions - Removed old generic action states, now handled by D3, D5, D7
  const [containmentActions, setContainmentActions] = useState([]); // D3
  const [correctiveActions, setCorrectiveActions] = useState([]); // D5
  const [preventiveActions, setPreventiveActions] = useState([]); // D7

  const [editingEightD, setEditingEightD] = useState(null);
  const [eightdForm, setEightdForm] = useState({
    problem_id: '',
    code: '',
    status: 'draft',
    owner_id: '',
    started_at: '',
    description: ''
  });
  const [nextEightDCode, setNextEightDCode] = useState('');

  // Team Members (D1)
  const [teamMemberForm, setTeamMemberForm] = useState({
    name: '',
    role: '',
    email: ''
  });
  const [editingMember, setEditingMember] = useState(null);
  const [selectedEightDForTeam, setSelectedEightDForTeam] = useState(null);
  const [showTeamMemberForm, setShowTeamMemberForm] = useState(false);

  // Five Whys (D4)
  const [whyTreeForm, setWhyTreeForm] = useState({
    method: '5whys',
    problem_statement: '',
    validated: false,
    nodes: [{ depth: 0, why_text: '',  category: '', parent_id: null, is_root_cause: false }]
  });
  const [editingWhyTree, setEditingWhyTree] = useState(null);
  const [selectedEightDForWhy, setSelectedEightDForWhy] = useState(null);
  const [showWhyTreeForm, setShowWhyTreeForm] = useState(false);
  
  // D3 - Containment Actions
  const [containmentForm, setContainmentForm] = useState({
    description: '',
    owner_id: '',
    due_at: '',
    status: 'open'
  });
  const [editingContainment, setEditingContainment] = useState(null);
  const [showContainmentForm, setShowContainmentForm] = useState(false);

  // D5 - Corrective Actions  
  const [correctiveForm, setCorrectiveForm] = useState({
    description: '',
    owner_id: '',
    due_at: '',
    status: 'open'
  });
  const [editingCorrective, setEditingCorrective] = useState(null);
  const [showCorrectiveForm, setShowCorrectiveForm] = useState(false);

  // D7 - Preventive Actions
  const [preventiveForm, setPreventiveForm] = useState({
    description: '',
    owner_id: '',
    due_at: '',
    status: 'open'
  });
  const [editingPreventive, setEditingPreventive] = useState(null);
  const [showPreventiveForm, setShowPreventiveForm] = useState(false);

  // Estado para el reporte 8D seleccionado (vista de detalle)
  const [selectedEightD, setSelectedEightD] = useState(null);

  const loadCatalogs = useCallback(async () => {
    try {
      const siteId = getCurrentSiteId();
      const params = siteId ? { site_id: siteId } : {};
      
      const { data } = await axios.get('/catalogs/all', { params });
      setLines(data.lines || []);
      setDefectTypes(data.defect_types || []);
      setProducts(data.products || []);
      
      const usersRes = await axios.get('/users/lookup', { params });
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (error) {
      console.error('Error loading catalogs:', error);
    }
  }, [getCurrentSiteId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const siteId = getCurrentSiteId();
      const params = siteId ? { site_id: siteId } : {};
      
      // Siempre cargar problemas para el selector de 8D
      const problemsRes = await axios.get('/problems', { params });
      setProblems(problemsRes.data);
      
      if (view === 'eightd') {
        const { data } = await axios.get('/eightd', { params });
        setEightdReports(data);
        // Cargar el próximo código 8D
        loadNextEightDCode();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [view, getCurrentSiteId]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {

      return;
    }
    
    // Check if user is loaded
    if (!user) {

      navigate('/login');
      return;
    }
    

    
    // Verify role permissions - permitir analyst y todos los roles admin
    const allowedRoles = ['analyst', 'admin_master', 'admin_site', 'admin_analytics'];
    if (!allowedRoles.includes(user.role)) {

      toast.error(t('acciones.messages.noPermissions') || 'No tienes permisos para acceder a esta página');
      navigate('/');
      return;
    }
    

    loadCatalogs();
    loadData();
  }, [authLoading, user, navigate, t, loadCatalogs, loadData]);

  // Recargar cuando cambia el sitio
  useEffect(() => {
    if (currentSite && user) {

      loadCatalogs();
      loadData();
    }
  }, [currentSite, user, loadCatalogs, loadData]);

  const loadNextEightDCode = async () => {
    try {
      const { data } = await axios.get('/eightd/next-code');
      setNextEightDCode(data.next_code);
    } catch (error) {
      console.error('Error loading next code:', error);
    }
  };

  // ========== PROBLEMAS ==========
  const onProblemChange = (e) => {
    const { name, value } = e.target;
    setProblemForm(prev => ({ ...prev, [name]: value }));
  };

  const generateProblemKey = () => {
    const { line_id, defect_type_id, product_id } = problemForm;
    return `L${line_id}_D${defect_type_id}_P${product_id || '0'}_${Date.now()}`;
  };

  // Verificar si existe un problema similar
  const checkDuplicateProblem = () => {
    if (!problemForm.line_id || !problemForm.defect_type_id || !problemForm.detected_at) {
      return null;
    }

    const duplicate = problems.find(p =>
      p.line_id === problemForm.line_id &&
      p.defect_type_id === problemForm.defect_type_id &&
      (p.product_id === problemForm.product_id || (!p.product_id && !problemForm.product_id)) &&
      p.detected_at?.split('T')[0] === problemForm.detected_at &&
      p.id !== editingProblem
    );    return duplicate;
  };

  const saveProblem = async () => {
    try {
      const siteId = getCurrentSiteId();
      
      // Limpiar campos vacíos y convertirlos a null
      const dataToSend = {
        line_id: problemForm.line_id || null,
        defect_type_id: problemForm.defect_type_id || null,
        product_id: problemForm.product_id || null,
        detected_at: problemForm.detected_at || new Date().toISOString().split('T')[0],
        period_start: problemForm.period_start || null,
        period_end: problemForm.period_end || null,
        status: problemForm.status || 'open',
        problem_key: problemForm.problem_key || generateProblemKey(),
        description: problemForm.description || null
      };

      // Agregar site_id si está disponible
      if (siteId) {
        dataToSend.site_id = siteId;
      }

      // Validar campos requeridos
      if (!dataToSend.line_id || !dataToSend.defect_type_id) {
        alert(t('acciones.messages.requiredFields'));
        return;
      }

      if (editingProblem) {
        await axios.put(`/problems/${editingProblem}`, dataToSend);
      } else {
        await axios.post('/problems', dataToSend);
      }
      
      resetProblemForm();
      loadData();
    } catch (error) {
      console.error('Error saving problem:', error);
      
      // Manejar error de duplicado (código 422)
      if (error.response?.status === 422 && error.response?.data?.message?.includes('already exists')) {
        alert(t('acciones.messages.duplicateProblem'));
      } else if (error.response?.status === 500 && error.response?.data?.message?.includes('Duplicate entry')) {
        alert(t('acciones.messages.duplicateProblem'));
      } else if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Mostrar errores de validación
        const errors = Object.values(error.response.data.errors).flat().join('\n');
        alert(errors);
      } else {
        alert(t('acciones.messages.errorSaving'));
      }
    }
  };

  const deleteProblem = async (id) => {
    if (!window.confirm(t('acciones.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`/problems/${id}`);
      loadData();
    } catch (error) {
      console.error('Error deleting problem:', error);
    }
  };

  const startEditProblem = (problem) => {
    setEditingProblem(problem.id);
    setProblemForm({
      line_id: problem.line_id || '',
      defect_type_id: problem.defect_type_id || '',
      product_id: problem.product_id || '',
      detected_at: problem.detected_at ? problem.detected_at.split('T')[0] : '',
      period_start: problem.period_start ? problem.period_start.split('T')[0] : '',
      period_end: problem.period_end ? problem.period_end.split('T')[0] : '',
      status: problem.status || 'open',
      problem_key: problem.problem_key || '',
      description: problem.description || ''
    });
  };

  const resetProblemForm = () => {
    setEditingProblem(null);
    setProblemForm({
      line_id: '',
      defect_type_id: '',
      product_id: '',
      detected_at: new Date().toISOString().split('T')[0],
      period_start: '',
      period_end: '',
      status: 'open',
      problem_key: '',
      description: ''
    });
  };

  // ========== 8D REPORTS ==========
  const onEightDChange = (e) => {
    const { name, value } = e.target;
    setEightdForm(prev => ({ ...prev, [name]: value }));
  };

  const saveEightD = async () => {
    try {
      const siteId = getCurrentSiteId();
      
      // Limpiar campos vacíos y convertirlos a null
      const dataToSend = {
        problem_id: eightdForm.problem_id || null,
        // No enviar código, se generará automáticamente en el backend
        status: eightdForm.status || 'draft',
        owner_id: eightdForm.owner_id || null,
        started_at: eightdForm.started_at || null,
        description: eightdForm.description || null
      };

      // Agregar site_id si está disponible
      if (siteId) {
        dataToSend.site_id = siteId;
      }

      // Validar solo problem_id (el código se genera automáticamente)
      if (!dataToSend.problem_id) {
        alert(t('acciones.messages.requiredFields8D'));
        return;
      }

      if (editingEightD) {
        await axios.put(`/eightd/${editingEightD}`, dataToSend);
      } else {
        await axios.post('/eightd', dataToSend);
      }
      
      resetEightDForm();
      loadData();
    } catch (error) {
      console.error('Error saving 8D:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert(t('acciones.messages.errorSaving'));
      }
    }
  };

  const deleteEightD = async (id) => {
    if (!window.confirm(t('acciones.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`/eightd/${id}`);
      loadData();
    } catch (error) {
      console.error('Error deleting 8D:', error);
    }
  };

  const startEditEightD = (report) => {
    setEditingEightD(report.id);
    setEightdForm({
      problem_id: report.problem_id || '',
      code: report.code || '',
      status: report.status || 'draft',
      owner_id: report.owner_id || '',
      started_at: report.started_at ? report.started_at.split('T')[0] : '',
      description: report.description || ''
    });
  };

  const resetEightDForm = () => {
    setEditingEightD(null);
    setEightdForm({
      problem_id: '',
      code: '',
      status: 'draft',
      owner_id: '',
      started_at: '',
      description: ''
    });
    // Recargar el próximo código
    loadNextEightDCode();
  };

  // ========== TEAM MEMBERS (D1) ==========
  const selectEightDReport = async (report) => {
    setSelectedEightD(report);
    setSelectedEightDForTeam(report.id);
    setSelectedEightDForWhy(report.id);
    // Cargar datos relacionados
    await loadTeamMembers(report.id);
    await loadWhyTrees(report.id);
    await loadActionsByType(report.id, 'containment');
    await loadActionsByType(report.id, 'corrective');
    await loadActionsByType(report.id, 'preventive');
    // Ocultar formularios por defecto
    setShowTeamMemberForm(false);
    setShowWhyTreeForm(false);
    setShowContainmentForm(false);
    setShowCorrectiveForm(false);
    setShowPreventiveForm(false);
  };

  const loadActionsByType = async (eightdId, type) => {
    try {
      const { data } = await axios.get(`/actions?eightd_id=${eightdId}`);
      const filtered = data.filter(action => action.type === type);
      
      if (type === 'containment') {
        setContainmentActions(filtered);
      } else if (type === 'corrective') {
        setCorrectiveActions(filtered);
      } else if (type === 'preventive') {
        setPreventiveActions(filtered);
      }
    } catch (error) {
      console.error(`Error loading ${type} actions:`, error);
    }
  };

  const loadTeamMembers = async (eightdId) => {
    try {
      const { data } = await axios.get(`/team-members?eightd_id=${eightdId}`);
      setTeamMembers(data);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const onTeamMemberChange = (e) => {
    const { name, value } = e.target;
    setTeamMemberForm(prev => ({ ...prev, [name]: value }));
  };

  const saveTeamMember = async () => {
    if (!selectedEightDForTeam) {
      alert(t('acciones.messages.selectEightD'));
      return;
    }

    if (!teamMemberForm.name || !teamMemberForm.role) {
      alert(t('acciones.messages.requiredFieldsTeam'));
      return;
    }

    try {
      const siteId = getCurrentSiteId();
      const payload = {
        eightd_id: selectedEightDForTeam,
        name: teamMemberForm.name,
        role: teamMemberForm.role,
        email: teamMemberForm.email || null,
        ...(siteId ? { site_id: siteId } : {})
      };

      if (editingMember) {
        await axios.put(`/team-members/${editingMember}`, payload);
      } else {
        await axios.post('/team-members', payload);
      }

      loadTeamMembers(selectedEightDForTeam);
      resetTeamMemberForm();
      setShowTeamMemberForm(false);
    } catch (error) {
      console.error('Error saving team member:', error);
      alert(t('acciones.messages.error'));
    }
  };

  const deleteTeamMember = async (id) => {
    if (!window.confirm(t('acciones.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`/team-members/${id}`);
      loadTeamMembers(selectedEightDForTeam);
    } catch (error) {
      console.error('Error deleting team member:', error);
    }
  };

  const startEditTeamMember = (member) => {
    setEditingMember(member.id);
    setTeamMemberForm({
      name: member.name,
      role: member.role,
      email: member.email || ''
    });
    setShowTeamMemberForm(true);
  };

  const resetTeamMemberForm = () => {
    setEditingMember(null);
    setTeamMemberForm({
      name: '',
      role: '',
      email: ''
    });
    setShowTeamMemberForm(false);
  };

  // ========== FIVE WHYS (D4) ==========
  const loadWhyTrees = async (eightdId) => {
    try {
      const { data } = await axios.get(`/why-trees?eightd_id=${eightdId}`);
      setWhyTrees(data);
    } catch (error) {
      console.error('Error loading why trees:', error);
    }
  };

  const onWhyTreeChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWhyTreeForm(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const onWhyNodeChange = (index, field, value) => {
    setWhyTreeForm(prev => ({
      ...prev,
      nodes: prev.nodes.map((node, i) => 
        i === index ? { ...node, [field]: value } : node
      )
    }));
  };

  const addWhyNode = () => {
    const currentLevel = whyTreeForm.nodes.length;
    if (currentLevel >= 5) {
      alert(t('acciones.messages.maxWhyLevels'));
      return;
    }
    
    setWhyTreeForm(prev => ({
      ...prev,
      nodes: [...prev.nodes, { 
        depth: currentLevel, 
        why_text: '', 
        category: '', 
        parent_id: null,
        is_root_cause: false
      }]
    }));
  };

  const removeWhyNode = (index) => {
    if (whyTreeForm.nodes.length <= 1) return;
    
    setWhyTreeForm(prev => ({
      ...prev,
      nodes: prev.nodes.filter((_, i) => i !== index).map((node, i) => ({
        ...node,
        depth: i
      }))
    }));
  };

  const saveWhyTree = async () => {
    if (!selectedEightDForWhy) {
      alert(t('acciones.messages.selectEightD'));
      return;
    }

    if (!whyTreeForm.problem_statement) {
      alert(t('acciones.messages.requiredFieldsWhy'));
      return;
    }

    try {
      const siteId = getCurrentSiteId();
      const payload = {
        eightd_id: selectedEightDForWhy,
        method: whyTreeForm.method || '5whys',
        problem_statement: whyTreeForm.problem_statement,
        validated: whyTreeForm.validated,
        nodes: whyTreeForm.nodes.filter(n => n.why_text.trim() !== '').map(n => ({
          why_text: n.why_text,
          category: n.category || null,
          depth: n.depth,
          parent_id: n.parent_id,
          is_root_cause: n.is_root_cause || false
        })),
        ...(siteId ? { site_id: siteId } : {})
      };

      if (editingWhyTree) {
        await axios.put(`/why-trees/${editingWhyTree}`, payload);
      } else {
        await axios.post('/why-trees', payload);
      }

      loadWhyTrees(selectedEightDForWhy);
      resetWhyTreeForm();
      setShowWhyTreeForm(false);
    } catch (error) {
      console.error('Error saving why tree:', error);
      alert(t('acciones.messages.error'));
    }
  };

  const deleteWhyTree = async (id) => {
    if (!window.confirm(t('acciones.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`/why-trees/${id}`);
      loadWhyTrees(selectedEightDForWhy);
    } catch (error) {
      console.error('Error deleting why tree:', error);
    }
  };

  const startEditWhyTree = (tree) => {
    console.log('startEditWhyTree called with:', tree);
    setEditingWhyTree(tree.id);
    console.log('Setting editingWhyTree to:', tree.id);
    const formData = {
      method: tree.method || '5whys',
      problem_statement: tree.problem_statement,
      validated: tree.validated,
      nodes: tree.nodes && tree.nodes.length > 0 
        ? tree.nodes.map(n => ({
            id: n.id,
            depth: n.depth,
            why_text: n.why_text,
            category: n.category || '',
            parent_id: n.parent_id,
            is_root_cause: n.is_root_cause
          }))
        : [{ depth: 0, why_text: '', category: '', parent_id: null, is_root_cause: false }]
    };
    console.log('Setting whyTreeForm to:', formData);
    setWhyTreeForm(formData);
    setShowWhyTreeForm(true);
    console.log('Setting showWhyTreeForm to: true');
  };

  const resetWhyTreeForm = () => {
    setEditingWhyTree(null);
    setWhyTreeForm({
      method: '5whys',
      problem_statement: '',
      validated: false,
      nodes: [{ depth: 0, why_text: '', category: '', parent_id: null, is_root_cause: false }]
    });
    setShowWhyTreeForm(false);
  };

  // ========== CONTAINMENT ACTIONS (D3) ==========
  const onContainmentChange = (e) => {
    const { name, value } = e.target;
    setContainmentForm(prev => ({ ...prev, [name]: value }));
  };

  const saveContainment = async () => {
    if (!selectedEightD) {
      alert(t('acciones.messages.selectEightD'));
      return;
    }

    if (!containmentForm.description) {
      alert('Por favor completa la descripción de la acción');
      return;
    }

    try {
      const siteId = getCurrentSiteId();
      const payload = {
        eightd_id: selectedEightD.id,
        type: 'containment',
        description: containmentForm.description,
        owner_id: containmentForm.owner_id || null,
        due_at: containmentForm.due_at || null,
        status: containmentForm.status || 'draft',
        ...(siteId ? { site_id: siteId } : {})
      };

      if (editingContainment) {
        await axios.put(`/actions/${editingContainment}`, payload);
      } else {
        await axios.post('/actions', payload);
      }

      loadActionsByType(selectedEightD.id, 'containment');
      resetContainmentForm();
      setShowContainmentForm(false);
    } catch (error) {
      console.error('Error saving containment action:', error);
      alert(t('acciones.messages.error'));
    }
  };

  const deleteContainment = async (id) => {
    if (!window.confirm(t('acciones.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`/actions/${id}`);
      loadActionsByType(selectedEightD.id, 'containment');
    } catch (error) {
      console.error('Error deleting containment action:', error);
    }
  };

  const startEditContainment = (action) => {
    setEditingContainment(action.id);
    setContainmentForm({
      description: action.description,
      owner_id: action.owner_id || '',
      due_at: action.due_at ? action.due_at.substring(0, 10) : '',
      status: action.status || 'draft'
    });
    setShowContainmentForm(true);
  };

  const resetContainmentForm = () => {
    setEditingContainment(null);
    setContainmentForm({
      description: '',
      owner_id: '',
      due_at: '',
      status: 'draft'
    });
    setShowContainmentForm(false);
  };

  // ========== CORRECTIVE ACTIONS (D5) ==========
  const onCorrectiveChange = (e) => {
    const { name, value } = e.target;
    setCorrectiveForm(prev => ({ ...prev, [name]: value }));
  };

  const saveCorrective = async () => {
    if (!selectedEightD) {
      alert(t('acciones.messages.selectEightD'));
      return;
    }

    if (!correctiveForm.description) {
      alert('Por favor completa la descripción de la acción');
      return;
    }

    try {
      const siteId = getCurrentSiteId();
      const payload = {
        eightd_id: selectedEightD.id,
        type: 'corrective',
        description: correctiveForm.description,
        owner_id: correctiveForm.owner_id || null,
        due_at: correctiveForm.due_at || null,
        status: correctiveForm.status || 'draft',
        ...(siteId ? { site_id: siteId } : {})
      };

      if (editingCorrective) {
        await axios.put(`/actions/${editingCorrective}`, payload);
      } else {
        await axios.post('/actions', payload);
      }

      loadActionsByType(selectedEightD.id, 'corrective');
      resetCorrectiveForm();
      setShowCorrectiveForm(false);
    } catch (error) {
      console.error('Error saving corrective action:', error);
      alert(t('acciones.messages.error'));
    }
  };

  const deleteCorrective = async (id) => {
    if (!window.confirm(t('acciones.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`/actions/${id}`);
      loadActionsByType(selectedEightD.id, 'corrective');
    } catch (error) {
      console.error('Error deleting corrective action:', error);
    }
  };

  const startEditCorrective = (action) => {
    setEditingCorrective(action.id);
    setCorrectiveForm({
      description: action.description,
      owner_id: action.owner_id || '',
      due_at: action.due_at ? action.due_at.substring(0, 10) : '',
      status: action.status || 'draft'
    });
    setShowCorrectiveForm(true);
  };

  const resetCorrectiveForm = () => {
    setEditingCorrective(null);
    setCorrectiveForm({
      description: '',
      owner_id: '',
      due_at: '',
      status: 'draft'
    });
    setShowCorrectiveForm(false);
  };

  // ========== PREVENTIVE ACTIONS (D7) ==========
  const onPreventiveChange = (e) => {
    const { name, value } = e.target;
    setPreventiveForm(prev => ({ ...prev, [name]: value }));
  };

  const savePreventive = async () => {
    if (!selectedEightD) {
      alert(t('acciones.messages.selectEightD'));
      return;
    }

    if (!preventiveForm.description) {
      alert('Por favor completa la descripción de la acción');
      return;
    }

    try {
      const siteId = getCurrentSiteId();
      const payload = {
        eightd_id: selectedEightD.id,
        type: 'preventive',
        description: preventiveForm.description,
        owner_id: preventiveForm.owner_id || null,
        due_at: preventiveForm.due_at || null,
        status: preventiveForm.status || 'draft',
        ...(siteId ? { site_id: siteId } : {})
      };

      if (editingPreventive) {
        await axios.put(`/actions/${editingPreventive}`, payload);
      } else {
        await axios.post('/actions', payload);
      }

      loadActionsByType(selectedEightD.id, 'preventive');
      resetPreventiveForm();
      setShowPreventiveForm(false);
    } catch (error) {
      console.error('Error saving preventive action:', error);
      alert(t('acciones.messages.error'));
    }
  };

  const deletePreventive = async (id) => {
    if (!window.confirm(t('acciones.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`/actions/${id}`);
      loadActionsByType(selectedEightD.id, 'preventive');
    } catch (error) {
      console.error('Error deleting preventive action:', error);
    }
  };

  const startEditPreventive = (action) => {
    setEditingPreventive(action.id);
    setPreventiveForm({
      description: action.description,
      owner_id: action.owner_id || '',
      due_at: action.due_at ? action.due_at.substring(0, 10) : '',
      status: action.status || 'draft'
    });
    setShowPreventiveForm(true);
  };

  const resetPreventiveForm = () => {
    setEditingPreventive(null);
    setPreventiveForm({
      description: '',
      owner_id: '',
      due_at: '',
      status: 'draft'
    });
    setShowPreventiveForm(false);
  };

  // ========== VERIFICATION FUNCTIONS ==========
  const verifyAction = async (action, type) => {
    try {
      const isVerified = !!action.verified_at;
      
      if (!isVerified) {
        // Verificar acción
        const evidence = prompt('Agregar evidencia de verificación (opcional):');
        const evidenceArray = evidence ? [{ description: evidence, type: 'text' }] : [];
        
        await axios.post(`/actions/${action.id}/verify`, {
          verified: true,
          evidence: evidenceArray
        });
        toast.success('Acción verificada exitosamente');
      } else {
        // Desverificar acción
        if (!window.confirm('¿Desea desverificar esta acción?')) return;
        
        await axios.post(`/actions/${action.id}/verify`, {
          verified: false,
          evidence: action.evidence || []
        });
        toast.success('Acción desverificada');
      }
      
      // Recargar acciones del tipo correspondiente
      loadActionsByType(selectedEightD.id, type);
    } catch (error) {
      console.error('Error verifying action:', error);
      toast.error('Error al verificar la acción');
    }
  };

  // ========== STATUS & HELPERS ==========

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-yellow-100 text-yellow-800',
      under_analysis: 'bg-blue-100 text-blue-800',
      containment: 'bg-orange-100 text-orange-800',
      corrective: 'bg-purple-100 text-purple-800',
      monitoring: 'bg-indigo-100 text-indigo-800',
      closed: 'bg-green-100 text-green-800',
      in_progress: 'bg-blue-100 text-blue-800',
      done: 'bg-green-100 text-green-800',
      verified: 'bg-teal-100 text-teal-800',
      draft: 'bg-gray-100 text-gray-800',
      implemented: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Mostrar loading mientras se verifica autenticación
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('acciones.messages.checkingPermissions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <h1 className="text-3xl font-extrabold text-blue-900 mb-6">{t('acciones.title')}</h1>

      {/* Badge del sitio actual */}
      {currentSite && (
        <div className="mb-4 inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-semibold">
          📍 {currentSite.code} - {currentSite.name}
        </div>
      )}

      {/* Tabs de navegación */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setView('problems')}
          className={`px-6 py-3 font-semibold rounded-lg transition ${
            view === 'problems'
              ? 'bg-blue-700 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('acciones.tabs.problems')}
        </button>
        <button
          onClick={() => setView('eightd')}
          className={`px-6 py-3 font-semibold rounded-lg transition ${
            view === 'eightd'
              ? 'bg-blue-700 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('acciones.tabs.eightd')}
        </button>
      </div>

      {/* ========== PROBLEMAS VIEW ========== */}
      {view === 'problems' && (
        <>
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-blue-100">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('acciones.problemForm.title')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.problemForm.line')}
                </label>
                <select
                  name="line_id"
                  value={problemForm.line_id}
                  onChange={onProblemChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">{t('acciones.problemForm.selectLine')}</option>
                  {lines.map(line => (
                    <option key={line.id} value={line.id}>{line.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.problemForm.defectType')}
                </label>
                <select
                  name="defect_type_id"
                  value={problemForm.defect_type_id}
                  onChange={onProblemChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">{t('acciones.problemForm.selectDefectType')}</option>
                  {defectTypes.map(dt => (
                    <option key={dt.id} value={dt.id}>{dt.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.problemForm.product')}
                </label>
                <select
                  name="product_id"
                  value={problemForm.product_id}
                  onChange={onProblemChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">{t('acciones.problemForm.selectProduct')}</option>
                  {products.map(prod => (
                    <option key={prod.id} value={prod.id}>{prod.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.problemForm.detectedAt')}
                </label>
                <input
                  type="date"
                  name="detected_at"
                  value={problemForm.detected_at}
                  onChange={onProblemChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.problemForm.status')}
                </label>
                <select
                  name="status"
                  value={problemForm.status}
                  onChange={onProblemChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="open">{t('acciones.status.open')}</option>
                  <option value="under_analysis">{t('acciones.status.under_analysis')}</option>
                  <option value="containment">{t('acciones.status.containment')}</option>
                  <option value="corrective">{t('acciones.status.corrective')}</option>
                  <option value="monitoring">{t('acciones.status.monitoring')}</option>
                  <option value="closed">{t('acciones.status.closed')}</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                {t('acciones.problemForm.description')}
              </label>
              <textarea
                name="description"
                value={problemForm.description}
                onChange={onProblemChange}
                rows="3"
                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* Advertencia de duplicado */}
            {!editingProblem && checkDuplicateProblem() && (
              <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">
                      {t('acciones.messages.duplicateWarning')}
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      {t('acciones.messages.duplicateWarningDetail')} (ID: {checkDuplicateProblem().id})
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={saveProblem}
                className="px-6 py-2 bg-green-600 text-white font-bold rounded-lg shadow hover:bg-green-700 transition"
              >
                {editingProblem ? t('acciones.buttons.update') : t('acciones.buttons.create')}
              </button>
              {editingProblem && (
                <button
                  onClick={resetProblemForm}
                  className="px-6 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
                >
                  {t('acciones.buttons.cancel')}
                </button>
              )}
            </div>
          </div>

          {/* Tabla de Problemas */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('acciones.problemList.title')}</h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('common.loading')}...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border border-gray-200 rounded-xl">
                  <thead className="bg-blue-100 text-blue-900">
                    <tr>
                      <th className="px-4 py-2 font-bold">{t('acciones.problemList.id')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.problemList.line')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.problemList.defectType')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.problemList.product')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.problemList.detectedAt')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.problemList.status')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.problemList.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {problems.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          {t('acciones.messages.noProblems')}
                        </td>
                      </tr>
                    ) : (
                      problems.map(problem => (
                        <tr key={problem.id} className="even:bg-gray-50">
                          <td className="px-4 py-2">{problem.id}</td>
                          <td className="px-4 py-2">{problem.line?.name || '-'}</td>
                          <td className="px-4 py-2">{problem.defect_type?.name || '-'}</td>
                          <td className="px-4 py-2">{problem.product?.name || '-'}</td>
                          <td className="px-4 py-2">{problem.detected_at?.split('T')[0]}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(problem.status)}`}>
                              {t(`acciones.status.${problem.status}`)}
                            </span>
                          </td>
                          <td className="px-4 py-2 flex gap-2">
                            <button
                              onClick={() => startEditProblem(problem)}
                              className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg font-semibold hover:bg-blue-100 transition"
                            >
                              {t('acciones.buttons.edit')}
                            </button>
                            <button
                              onClick={() => deleteProblem(problem.id)}
                              className="px-3 py-1 bg-red-50 text-red-700 border border-red-300 rounded-lg font-semibold hover:bg-red-100 transition"
                            >
                              {t('acciones.buttons.delete')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ========== 8D REPORTS VIEW ========== */}
      {view === 'eightd' && (
        <>
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-blue-100">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('acciones.eightdForm.title')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.eightdForm.problem')}
                </label>
                <select
                  name="problem_id"
                  value={eightdForm.problem_id}
                  onChange={onEightDChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">{t('acciones.eightdForm.selectProblem')}</option>
                  {problems.map(prob => (
                    <option key={prob.id} value={prob.id}>
                      #{prob.id} - {prob.line?.name} - {prob.defect_type?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.eightdForm.code')}
                </label>
                <input
                  type="text"
                  name="code"
                  value={eightdForm.code || nextEightDCode}
                  readOnly
                  disabled
                  placeholder="8D-2025-001"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-500 bg-gray-50 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.eightdForm.owner')}
                </label>
                <select
                  name="owner_id"
                  value={eightdForm.owner_id}
                  onChange={onEightDChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">{t('acciones.eightdForm.selectOwner')}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.username}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.eightdForm.status')}
                </label>
                <select
                  name="status"
                  value={eightdForm.status}
                  onChange={onEightDChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="draft">{t('acciones.status.draft')}</option>
                  <option value="in_progress">{t('acciones.status.in_progress')}</option>
                  <option value="implemented">{t('acciones.status.implemented')}</option>
                  <option value="verified">{t('acciones.status.verified')}</option>
                  <option value="closed">{t('acciones.status.closed')}</option>
                  <option value="cancelled">{t('acciones.status.cancelled')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  {t('acciones.eightdForm.startedAt')}
                </label>
                <input
                  type="date"
                  name="started_at"
                  value={eightdForm.started_at}
                  onChange={onEightDChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={saveEightD}
                className="px-6 py-2 bg-green-600 text-white font-bold rounded-lg shadow hover:bg-green-700 transition"
              >
                {editingEightD ? t('acciones.buttons.update') : t('acciones.buttons.create')}
              </button>
              {editingEightD && (
                <button
                  onClick={resetEightDForm}
                  className="px-6 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
                >
                  {t('acciones.buttons.cancel')}
                </button>
              )}
            </div>
          </div>

          {/* Tabla de 8D Reports */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('acciones.eightdList.title')}</h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('common.loading')}...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border border-gray-200 rounded-xl">
                  <thead className="bg-blue-100 text-blue-900">
                    <tr>
                      <th className="px-4 py-2 font-bold">{t('acciones.eightdList.code')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.eightdList.problem')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.eightdList.owner')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.eightdList.status')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.eightdList.startedAt')}</th>
                      <th className="px-4 py-2 font-bold">{t('acciones.eightdList.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eightdReports.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          {t('acciones.messages.noEightD')}
                        </td>
                      </tr>
                    ) : (
                      eightdReports.map(report => (
                        <tr key={report.id} className="even:bg-gray-50">
                          <td className="px-4 py-2 font-semibold">{report.code}</td>
                          <td className="px-4 py-2">
                            #{report.problem?.id} - {report.problem?.line?.name}
                          </td>
                          <td className="px-4 py-2">{report.owner?.username || '-'}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(report.status)}`}>
                              {t(`acciones.status.${report.status}`)}
                            </span>
                          </td>
                          <td className="px-4 py-2">{report.started_at?.split('T')[0] || '-'}</td>
                          <td className="px-4 py-2 flex gap-2">
                            <button
                              onClick={() => selectEightDReport(report)}
                              className="px-3 py-1 bg-green-50 text-green-700 border border-green-300 rounded-lg font-semibold hover:bg-green-100 transition"
                            >
                              📋 {t('acciones.buttons.viewDetail')}
                            </button>
                            <button
                              onClick={() => startEditEightD(report)}
                              className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg font-semibold hover:bg-blue-100 transition"
                            >
                              {t('acciones.buttons.edit')}
                            </button>
                            <button
                              onClick={() => deleteEightD(report.id)}
                              className="px-3 py-1 bg-red-50 text-red-700 border border-red-300 rounded-lg font-semibold hover:bg-red-100 transition"
                            >
                              {t('acciones.buttons.delete')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ========== D1 - TEAM MEMBERS ========== */}
          <TeamMembers
            selectedEightD={selectedEightD}
            teamMembers={teamMembers}
            showForm={showTeamMemberForm}
            setShowForm={setShowTeamMemberForm}
            editingMember={editingMember}
            teamMemberForm={teamMemberForm}
            onFormChange={onTeamMemberChange}
            onSave={saveTeamMember}
            onDelete={deleteTeamMember}
            onEdit={startEditTeamMember}
            onReset={resetTeamMemberForm}
            currentSite={currentSite}
          />

          {/* ========== D3 - CONTAINMENT ACTIONS ========== */}
          <ActionsSection
            selectedEightD={selectedEightD}
            actions={containmentActions}
            showForm={showContainmentForm}
            setShowForm={setShowContainmentForm}
            editingAction={editingContainment}
            actionForm={containmentForm}
            onFormChange={onContainmentChange}
            onSave={saveContainment}
            onDelete={deleteContainment}
            onEdit={startEditContainment}
            onReset={resetContainmentForm}
            onVerify={(action) => verifyAction(action, 'containment')}
            users={users}
            type="containment"
            color="yellow"
            disciplineNumber="D3"
            currentSite={currentSite}
          />

          {/* ========== D4 - FIVE WHYS ========== */}
          <FiveWhys
            selectedEightD={selectedEightD}
            whyTrees={whyTrees}
            showForm={showWhyTreeForm}
            setShowForm={setShowWhyTreeForm}
            editingWhyTree={editingWhyTree}
            whyTreeForm={whyTreeForm}
            onFormChange={onWhyTreeChange}
            onNodeChange={onWhyNodeChange}
            onAddNode={addWhyNode}
            onRemoveNode={removeWhyNode}
            onSave={saveWhyTree}
            onDelete={deleteWhyTree}
            onEdit={startEditWhyTree}
            onReset={resetWhyTreeForm}
            currentSite={currentSite}
          />

          {/* ========== D5 - CORRECTIVE ACTIONS ========== */}
          <ActionsSection
            selectedEightD={selectedEightD}
            actions={correctiveActions}
            showForm={showCorrectiveForm}
            setShowForm={setShowCorrectiveForm}
            editingAction={editingCorrective}
            actionForm={correctiveForm}
            onFormChange={onCorrectiveChange}
            onSave={saveCorrective}
            onDelete={deleteCorrective}
            onEdit={startEditCorrective}
            onReset={resetCorrectiveForm}
            onVerify={(action) => verifyAction(action, 'corrective')}
            users={users}
            type="corrective"
            color="green"
            disciplineNumber="D5"
            currentSite={currentSite}
          />

          {/* ========== D6 - VERIFICATION & ANALYSIS ========== */}
          <D6Verification
            selectedEightD={selectedEightD}
            currentSite={currentSite}
          />

          {/* ========== D7 - PREVENTIVE ACTIONS ========== */}
          <ActionsSection
            selectedEightD={selectedEightD}
            actions={preventiveActions}
            showForm={showPreventiveForm}
            setShowForm={setShowPreventiveForm}
            editingAction={editingPreventive}
            actionForm={preventiveForm}
            onFormChange={onPreventiveChange}
            onSave={savePreventive}
            onDelete={deletePreventive}
            onEdit={startEditPreventive}
            onReset={resetPreventiveForm}
            onVerify={(action) => verifyAction(action, 'preventive')}
            users={users}
            type="preventive"
            color="blue"
            disciplineNumber="D7"
            currentSite={currentSite}
          />
        </>
      )}
    </div>
  );
};


export default Acciones;
